import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  cn,
} from "@exhousing/design-system";
import { Plus, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { ContactFieldsEditor } from "@/components/party/ContactFieldsEditor";
import {
  useCreateGuarantor,
  useCreateLandlord,
  useCreateTenant,
  useGuarantors,
  useLandlords,
  useTenants,
} from "@/hooks/api/useParties";
import { buildContactPayload } from "@/lib/contactFields";

type Tab = "landlords" | "tenants" | "guarantors";

const TABS: Tab[] = ["landlords", "tenants", "guarantors"];
const SINGULAR: Record<Tab, string> = {
  landlords: "landlord",
  tenants: "tenant",
  guarantors: "guarantor",
};

const SELECT = "flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm";
const TENANT_TYPES = ["local", "expat", "sharers", "student", "gv"];
const ID_TYPES = ["passport", "id_card", "driver_license"];

const emptyContact = (): Record<string, string> => ({
  title: "",
  gender: "",
  language: "nl",
  first_name: "",
  middle_name: "",
  last_name: "",
  company_name: "",
  email: "",
  phone: "",
  mobile: "",
  street: "",
  house_number: "",
  postal_code: "",
  city: "",
});

export function PartiesPage() {
  const [tab, setTab] = useState<Tab>("landlords");
  const landlords = useLandlords();
  const tenants = useTenants();
  const guarantors = useGuarantors();
  const createLandlord = useCreateLandlord();
  const createTenant = useCreateTenant();
  const createGuarantor = useCreateGuarantor();
  const [showForm, setShowForm] = useState(false);

  const [contact, setContact] = useState<Record<string, string>>(emptyContact);
  const [ll, setLl] = useState({
    landlord_type: "private",
    category: "",
    iban: "",
    bank_account_name: "",
    brokerage_commission: "",
    coc_number: "",
    birth_date: "",
    first_contact_at: "",
  });
  const [llBrokerage, setLlBrokerage] = useState(false);
  const [llThirdParty, setLlThirdParty] = useState(false);
  const [tn, setTn] = useState({
    tenant_type: "",
    identification_type: "",
    identification_number: "",
    iban: "",
    bank_account_name: "",
  });
  const [tnSubscriber, setTnSubscriber] = useState(false);
  const [gn, setGn] = useState({
    income_type: "",
    gross_monthly_income: "",
    relationship_to_tenant: "",
    iban: "",
    bank_account_name: "",
  });

  const setC = (k: string, v: string) => setContact((s) => ({ ...s, [k]: v }));

  const reset = () => {
    setContact(emptyContact());
    setLl({
      landlord_type: "private",
      category: "",
      iban: "",
      bank_account_name: "",
      brokerage_commission: "",
      coc_number: "",
      birth_date: "",
      first_contact_at: "",
    });
    setLlBrokerage(false);
    setLlThirdParty(false);
    setTn({
      tenant_type: "",
      identification_type: "",
      identification_number: "",
      iban: "",
      bank_account_name: "",
    });
    setTnSubscriber(false);
    setGn({
      income_type: "",
      gross_monthly_income: "",
      relationship_to_tenant: "",
      iban: "",
      bank_account_name: "",
    });
    setShowForm(false);
  };

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const opts = {
      onSuccess: () => {
        toast.success(`${SINGULAR[tab][0].toUpperCase()}${SINGULAR[tab].slice(1)} added`);
        reset();
      },
      onError: (err: Error) => toast.error(err.message),
    };
    if (tab === "landlords") {
      const kind = ll.landlord_type === "company" ? "company" : "person";
      createLandlord.mutate(
        {
          contact: buildContactPayload(contact, kind),
          landlord_type: ll.landlord_type,
          category: ll.category || null,
          iban: ll.iban || null,
          bank_account_name: ll.bank_account_name || null,
          brokerage_commission: ll.brokerage_commission || null,
          has_brokerage_agreement: llBrokerage,
          is_third_party: llThirdParty,
          coc_number: ll.coc_number || null,
          birth_date: ll.birth_date || null,
          first_contact_at: ll.first_contact_at || null,
        },
        opts,
      );
    } else if (tab === "tenants") {
      createTenant.mutate(
        {
          contact: buildContactPayload(contact, "person"),
          tenant_type: tn.tenant_type || null,
          identification_type: tn.identification_type || null,
          identification_number: tn.identification_number || null,
          iban: tn.iban || null,
          bank_account_name: tn.bank_account_name || null,
          is_subscriber: tnSubscriber,
        },
        opts,
      );
    } else {
      createGuarantor.mutate(
        {
          contact: buildContactPayload(contact, "person"),
          income_type: gn.income_type || null,
          gross_monthly_income: gn.gross_monthly_income
            ? Number(gn.gross_monthly_income)
            : null,
          relationship_to_tenant: gn.relationship_to_tenant || null,
          iban: gn.iban || null,
          bank_account_name: gn.bank_account_name || null,
        },
        opts,
      );
    }
  };

  const rows =
    tab === "landlords"
      ? (landlords.data ?? [])
      : tab === "tenants"
        ? (tenants.data ?? [])
        : (guarantors.data ?? []);
  const pending =
    createLandlord.isPending || createTenant.isPending || createGuarantor.isPending;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">
            Landlords &amp; Tenants
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {rows.length} {tab}
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="size-4" />
          New {SINGULAR[tab]}
        </Button>
      </header>

      <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "flex-1 rounded-md px-4 py-2 text-sm font-medium capitalize transition-colors",
              tab === t
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {showForm && (
        <Card className="animate-fade-in-up">
          <CardContent className="space-y-5 pt-6">
            <form onSubmit={onCreate} className="space-y-5">
              <ContactFieldsEditor form={contact} set={setC} />

              {tab === "landlords" && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="landlord_type">Type</Label>
                    <select
                      id="landlord_type"
                      value={ll.landlord_type}
                      onChange={(e) => setLl({ ...ll, landlord_type: e.target.value })}
                      className={SELECT}
                    >
                      <option value="private">Private</option>
                      <option value="company">Company</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="category">Category tier</Label>
                    <select
                      id="category"
                      value={ll.category}
                      onChange={(e) => setLl({ ...ll, category: e.target.value })}
                      className={SELECT}
                    >
                      <option value="">—</option>
                      {["A", "B", "C", "D"].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Field label="IBAN" value={ll.iban} onChange={(v) => setLl({ ...ll, iban: v })} />
                  <Field label="Account holder" value={ll.bank_account_name} onChange={(v) => setLl({ ...ll, bank_account_name: v })} />
                  <Field label="Brokerage commission" value={ll.brokerage_commission} onChange={(v) => setLl({ ...ll, brokerage_commission: v })} />
                  <Field label="KVK / CoC number" value={ll.coc_number} onChange={(v) => setLl({ ...ll, coc_number: v })} />
                  <Field label="Birth date" type="date" value={ll.birth_date} onChange={(v) => setLl({ ...ll, birth_date: v })} />
                  <Field label="First contact" type="date" value={ll.first_contact_at} onChange={(v) => setLl({ ...ll, first_contact_at: v })} />
                  <div className="flex flex-wrap items-center gap-4 sm:col-span-3">
                    <Check label="Brokerage agreement" checked={llBrokerage} onChange={setLlBrokerage} />
                    <Check label="Third party (managing agent)" checked={llThirdParty} onChange={setLlThirdParty} />
                  </div>
                </div>
              )}

              {tab === "tenants" && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="tenant_type">Tenant type</Label>
                    <select
                      id="tenant_type"
                      value={tn.tenant_type}
                      onChange={(e) => setTn({ ...tn, tenant_type: e.target.value })}
                      className={SELECT}
                    >
                      <option value="">—</option>
                      {TENANT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="identification_type">ID type</Label>
                    <select
                      id="identification_type"
                      value={tn.identification_type}
                      onChange={(e) => setTn({ ...tn, identification_type: e.target.value })}
                      className={SELECT}
                    >
                      <option value="">—</option>
                      {ID_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Field label="ID number" value={tn.identification_number} onChange={(v) => setTn({ ...tn, identification_number: v })} />
                  <Field label="IBAN" value={tn.iban} onChange={(v) => setTn({ ...tn, iban: v })} />
                  <Field label="Account holder" value={tn.bank_account_name} onChange={(v) => setTn({ ...tn, bank_account_name: v })} />
                  <div className="flex items-center sm:col-span-3">
                    <Check label="Subscriber (receives matching listings)" checked={tnSubscriber} onChange={setTnSubscriber} />
                  </div>
                </div>
              )}

              {tab === "guarantors" && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="Income type" value={gn.income_type} onChange={(v) => setGn({ ...gn, income_type: v })} />
                  <Field label="Gross monthly income" type="number" value={gn.gross_monthly_income} onChange={(v) => setGn({ ...gn, gross_monthly_income: v })} />
                  <Field label="Relationship to tenant" value={gn.relationship_to_tenant} onChange={(v) => setGn({ ...gn, relationship_to_tenant: v })} />
                  <Field label="IBAN" value={gn.iban} onChange={(v) => setGn({ ...gn, iban: v })} />
                  <Field label="Account holder" value={gn.bank_account_name} onChange={(v) => setGn({ ...gn, bank_account_name: v })} />
                </div>
              )}

              <div className="flex gap-2">
                <Button type="submit" disabled={pending}>
                  {pending ? "Saving…" : `Create ${SINGULAR[tab]}`}
                </Button>
                <Button type="button" variant="ghost" onClick={reset}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {rows.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">
                    {tab === "landlords"
                      ? "Type"
                      : tab === "tenants"
                        ? "Subscriber"
                        : "Relationship"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="px-6 py-3 font-medium">
                      <Link
                        to={`/parties/${tab}/${r.id}`}
                        className="text-primary hover:underline"
                      >
                        {r.contact.display_name}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {r.contact.email ?? "—"}
                    </td>
                    <td className="px-6 py-3">
                      {tab === "landlords" ? (
                        <Badge tone="brand">
                          {"landlord_type" in r ? r.landlord_type : ""}
                        </Badge>
                      ) : tab === "tenants" ? (
                        <Badge tone={"is_subscriber" in r && r.is_subscriber ? "success" : "neutral"}>
                          {"is_subscriber" in r && r.is_subscriber ? "Yes" : "No"}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          {"relationship_to_tenant" in r && r.relationship_to_tenant
                            ? r.relationship_to_tenant
                            : "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <Users className="size-8 opacity-40" />
              <p className="text-sm">No {tab} yet.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border-input accent-primary"
      />
      {label}
    </label>
  );
}
